/**
 * Resolves the Google OAuth client id from the redux store, falling back to the
 * hardcoded per-origin map when the store has no value.
 */
import { clientIdGoogle } from 'app/config/config';
import * as coreEntities from 'core/store/index';
import { useSelector } from 'react-redux';

// Last-resort default: the generic OneClick client id (the one served on
// tangerine-latest), mirroring the getClientIdMicrosoft fallback. Origins not
// listed in GCP's authorized origins will still fail inside Google's popup,
// but with the managed client id instead of an empty one.
const DEFAULT_CLIENT_ID_ORIGIN = 'https://tangerine-latest.oneclick.es';

// The app/config map is the single fallback source: it already contains every
// Edelvives origin with the same client ids (the login always used it, in
// Edelvives builds too), so no per-tenant map selection is needed.
function getFallbackClientId(): string {
  const originMap: Record<string, string> = clientIdGoogle;

  return originMap[window.location.origin] ?? originMap[DEFAULT_CLIENT_ID_ORIGIN] ?? '';
}

/**
 * Reads the `getGoogleClientId` selector and falls back to the origin map when
 * it returns a falsy value. Non-React consumers should receive the resolved
 * clientId as a parameter from a calling hook/component instead.
 */
export function useGoogleClientId(): string {
  const googleClientId = useSelector((state: unknown) => coreEntities.organization.selectors.getGoogleClientId(state));

  return googleClientId || getFallbackClientId();
}
